import { Range, WorkSheet, utils } from 'xlsx';
import { MapOption } from './import-wizard';
import { ImportProfile } from './types';

const { encode_cell } = utils;

function extractRowAsObject(range: Range, ws: WorkSheet, R: number, header: string[]) {
	const row = {};
	let empty = 0;
	/* walk the rows */
	for (var C = range.s.c; C <= range.e.c; ++C) {
		// date formts is annoying. w appears to be a cached formatted value that I want to ignore
		var cell = ws[encode_cell({ r: R, c: C })] ?? {};

		//
		const value = utils.format_cell(cell, undefined, { dateNF: 'yyyy-mm-dd hh:MM:ss' });

		const h = header[C - range.s.c] ?? utils.encode_col(C);
		row[h] = value;
		if (!value) empty++;
	}
	// we go from s to <= end (so we need to add 1)
	if (range.e.c - range.s.c + 1 === empty) return null;
	return row;
}

export function extractHeader(ws: WorkSheet, headerRow: boolean, skip: number = 0) {
	const range = utils.decode_range(ws['!ref']);
	if (!headerRow) {
		const row: string[] = [];
		for (let c = range.s.c; c <= range.e.c; c++) row.push(utils.encode_col(c));

		return row;
	}

	const header = [];

	// Can't go out of bounds
	if (range.s.r + skip > range.e.r) return header;

	for (var C = range.s.c; C <= range.e.c; ++C) {
		var cell = ws[utils.encode_cell({ r: range.s.r + skip, c: C })];
		const value = cell?.v ?? utils.encode_col(C);
		if (header.includes(value)) header[C] = `${value} (${utils.encode_col(C)})`;
		header[C] = value;
	}

	return makeUniqueArray(header);
}

export function extractData({
	ws,
	hasHeader,
	header,
	rowLimit,
	skipRowsStart,
	skipRowsEnd
}: {
	ws: WorkSheet;
	hasHeader: boolean;
	header?: string[];
	rowLimit?: number;
	skipRowsStart?: number;
	skipRowsEnd?: number;
}) {
	if (!header) header = extractHeader(ws, hasHeader, skipRowsStart) as string[];

	const range = utils.decode_range(ws['!ref']);
	const out: Record<string, string>[] = [];

	/* walk the columns */
	const startRowIndex = range.s.r + (hasHeader ? 1 : 0) + (skipRowsStart ?? 0);
	const endRowIndex = range.e.r - (skipRowsEnd ?? 0);

	for (let R = startRowIndex; R <= endRowIndex; ++R) {
		const row = extractRowAsObject(range, ws, R, header);
		// if all the values are empty, we will return null and we skip over it here
		if (!row) continue;
		out.push(row);
		if (typeof rowLimit === 'number' && out.length >= rowLimit) break;
	}
	return out;
}
/** Returns an array of unique items, each duplicate found is numbered with (current count) appended */
export function makeUniqueArray(arr: string[]): string[] {
	const counts = {};
	const result = [];
	for (const item of arr) {
		if (counts[item] === undefined) {
			counts[item] = 1;
			result.push(item);
		} else {
			counts[item]++;
			result.push(`${item}(${counts[item]})`);
		}
	}
	return result;
}
/**
 * Removes any spaces, hypens, underscores from input
 * @param input - Input string to modify
 */
const replaceCharacters = (input: string): string => {
	return typeof input === 'string' ? input.toLowerCase().replaceAll(/[\s\-_]/g, '') : input;
};

const HEADER_ALIAS = {
	organisation: ['company', 'name', 'org', 'organization'],
	id: ['swiftfoxid'],
	landline: ['phone']
};

/**
 * Attempts to match columns either automatically or by the optionally provided profile
 * @param header - Array of header fields from the data file
 * @param setMapping - Function to set mapping
 * @param mapTypes - List of map types/options
 * @param mapRef - The mapRef Record (used to check if match already exists to prevent duplicates)
 * @param profile - Your optional profile containing mappings
 */
export function tryMatchColumns(
	header: string[],
	setMapping: (key: string, value: string) => void,
	mapTypes: readonly MapOption[],
	mapRef: Record<string, string>,
	profile?: ImportProfile
) {
	if (profile?.mapping) {
		for (const h of profile?.mapping) {
			const existsInHeader = header.includes(h.headerName);

			// We only want to add the field if it is in the current sheet
			if (existsInHeader) {
				setMapping(h.headerName, h.matchKey);
			}
		}
	} else {
		// Try auto match if no profile mapping set
		for (const h of header) {
			const cleanedH = replaceCharacters(h);
			if (!h || typeof h !== 'string') return;
			const match = mapTypes.find(
				m =>
					m.key === cleanedH ||
					replaceCharacters(m.title) === cleanedH ||
					(HEADER_ALIAS[m.key]?.includes(cleanedH) && !header.includes(m.key))
			);
			if (match) {
				const exists = Object.values(mapRef).includes(match.key);
				if (!exists) setMapping(h, match.key);
			}
		}
	}
}
/**
 * Used to compare your mapRef with requiredFields and returns if all requiredFields are included. Also compares subarrays of optional fields where only one field in subarray must be present.
 * Note: This only checks they are present, not that they are valid.
 * @param requiredFields - Your array of required fields
 * @param mapRef - Your Record of mappings
 */
export function checkRequiredFields(requiredFields: (string | string[])[], mapRef: Record<string, string>) {
	const selectedFields = Object.values(mapRef);

	return requiredFields.every(a => {
		if (typeof a === 'string') {
			return selectedFields.includes(a);
		} else {
			return a.some(subA => selectedFields.includes(subA));
		}
	});
}
